import React, { useState, useRef, useCallback } from 'react';
import { X, ZoomIn, ZoomOut } from 'lucide-react';

const ProductCard = ({ produto }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const posStart = useRef({ x: 0, y: 0 });

  const getWhatsAppUrl = () => {
    const phone = '351918288122';
    const message = encodeURIComponent(
      `Olá Santa Carne! Gostaria de pedir um orçamento para: *${produto.nome}*${produto.preco ? ` (${produto.preco})` : ''}. Podem ajudar-me?`
    );
    return `https://wa.me/${phone}?text=${message}`;
  };

  const openLightbox = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const handleZoom = useCallback((delta) => {
    setScale(prev => {
      const next = prev + delta;
      if (next < 1) {
        setPosition({ x: 0, y: 0 });
        return 1;
      }
      return Math.min(next, 5);
    });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    handleZoom(e.deltaY < 0 ? 0.3 : -0.3);
  }, [handleZoom]);

  const handlePointerDown = (e) => {
    if (scale <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    posStart.current = { ...position };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPosition({ x: posStart.current.x + dx, y: posStart.current.y + dy });
  };

  const handlePointerUp = () => setDragging(false);

  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  };

  return (
    <>
      <div className="group rounded-lg overflow-hidden bg-[#242424] border border-[#B8860B]/10 hover:border-[#B8860B]/40 transition-all duration-500">
        <div
          className="relative overflow-hidden aspect-[4/3] cursor-zoom-in"
          onClick={openLightbox}
          data-testid={`product-image-wrapper-${produto.id}`}
        >
          <img
            src={produto.imagem}
            alt={produto.nome}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            data-testid={`product-image-${produto.id}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-60" />
          {produto.destaque && (
            <div className="absolute top-4 right-4 bg-[#B8860B] text-[#1a1a1a] px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded">
              Destaque
            </div>
          )}
          <div className="absolute bottom-3 right-3 bg-black/50 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-4 h-4 text-white" />
          </div>
        </div>
        <div className="p-6 space-y-4">
          <h3 className="text-xl font-semibold text-[#E8DDCB] group-hover:text-[#B8860B] transition-colors" style={{fontFamily: "'Playfair Display', serif"}}>
            {produto.nome}
          </h3>
          <p className="text-[#E8DDCB]/60 text-sm leading-relaxed">
            {produto.descricao}
          </p>
          {produto.preco && (
            <p className="text-[#B8860B] font-semibold text-sm">{produto.preco}</p>
          )}
          <a
            href={getWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`pedir-orcamento-${produto.id}`}
            className="w-full block text-center bg-transparent border border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B] hover:text-[#1a1a1a] font-medium tracking-wider uppercase text-xs transition-all py-2 px-4 rounded-md"
          >
            Pedir Orçamento
          </a>
        </div>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
          data-testid={`lightbox-${produto.id}`}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-10 bg-gradient-to-b from-black/80 to-transparent">
            <h3 className="text-[#E8DDCB] text-lg font-semibold truncate mr-4" style={{fontFamily: "'Playfair Display', serif"}}>
              {produto.nome}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleZoom(0.5)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                data-testid="lightbox-zoom-in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleZoom(-0.5)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                data-testid="lightbox-zoom-out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-white/60 text-sm min-w-[3rem] text-center">{Math.round(scale * 100)}%</span>
              <button
                onClick={closeLightbox}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors ml-2"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Image */}
          <div
            className="w-full h-full flex items-center justify-center overflow-hidden touch-none"
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
          >
            <img
              src={produto.imagem}
              alt={produto.nome}
              className="max-w-[90vw] max-h-[85vh] object-contain select-none"
              draggable={false}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: dragging ? 'none' : 'transform 0.2s ease-out',
              }}
              data-testid={`lightbox-image-${produto.id}`}
            />
          </div>

          {/* Footer hint */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white/40 text-xs">
              {scale <= 1 ? 'Clique duplo ou scroll para ampliar' : 'Arraste para mover • Clique duplo para resetar'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
